/**
 * JS Placement Tower Configuration
 * 
 * Explicit configuration for JS Placement images tower
 * Edit this file to:
 * - Select which images to use
 * - Group images together
 * - Set base height
 * - Set scale
 * - Configure gaps between layers
 */

export interface MaterialConfig {
  // Base color (hex string, e.g., '0xffffff' for white)
  color?: string;

  // Material properties
  roughness?: number; // 0.0 = mirror, 1.0 = matte
  metalness?: number; // 0.0 = dielectric, 1.0 = metal

  // Emissive properties
  emissive?: string; // Emissive color (hex string)
  emissiveIntensity?: number; // Emissive intensity multiplier

  // Transparency
  opacity?: number; // 0.0 = transparent, 1.0 = opaque
  alphaTest?: number; // Alpha test threshold (0.0-1.0)

  // Normal map scale
  normalScale?: number; // Normal map intensity (1.0 = default)

  // Vertex displacement (city/skyscraper effect)
  displacementAmount?: number; // Height displacement based on luminance (0.0 = no displacement)
  displacementSegments?: number; // Geometry subdivisions for displacement detail (default: 64)
}

export interface LayerConfig {
  // Explicit position in world space
  position: { x: number; y: number; z: number };

  // Rotation in degrees (applied around X, Y, Z axes)
  rotation?: { x?: number; y?: number; z?: number }; // Optional, defaults to 0 for each axis

  // Texture filenames
  seamless?: string; // Seamless variant filename
  seamlessNormal?: string; // Seamless normal map filename
  seamlessColor?: string; // Seamless color map filename
  raster?: string; // Base/diffuse texture filename
  normal?: string; // Normal map filename
  color?: string; // Color map filename
}

export interface SkyboxGradientConfig {
  // Blue color in HSL (hue in degrees, saturation 0-1, lightness 0-1)
  blueHue: number; // Currently 236.69
  blueSaturation: number; // Currently 0.506
  blueLightness: number; // Currently 0.4922

  // White color (RGB 0-1)
  whiteR: number; // Currently 1.0
  whiteG: number; // Currently 1.0
  whiteB: number; // Currently 1.0

  // Black color (RGB 0-1)
  blackR: number; // Currently 0.0
  blackG: number; // Currently 0.0
  blackB: number; // Currently 0.0

  // Gradient thresholds (Y position in UV space, 0-1)
  threshold1: number; // Currently 0.75
  threshold2: number; // Currently 0.5
  threshold3: number; // Currently 0.25
}

export interface TowerConfig {
  // Scale multiplier for all planes (1.0 = original size)
  scale: number;

  // Skybox scale (base radius is 85, so 1.0 = 85, 1.5 = 127.5, etc.)
  skyboxScale: number;

  // Skybox gradient configurations array
  skyboxGradients: SkyboxGradientConfig[];

  // Active skybox gradient index (selects which gradient to use)
  activeSkyboxGradientIndex: number;

  // Shared material for both layers (they use the same material)
  material: MaterialConfig;

  // Two explicit layers with explicit positions
  layer1: LayerConfig;
  layer2: LayerConfig;
}

/**
 * Tower configuration - SIMPLIFIED: Only two layers with explicit transforms
 */
export const TOWER_CONFIG: TowerConfig = {
  // Scale multiplier for all planes
  // Original plane size is 10 units, so:
  // 1.0 = 10 units, 0.5 = 5 units, 2.0 = 20 units, etc.
  scale: 30, // ≈ 27.6

  // Skybox scale (base radius is 85 units)
  // 1.0 = 85 units, 1.5 = 127.5 units, 2.0 = 170 units, etc.
  // OPTIMIZATION: Reduced from 35 to 3 for better performance on MacBooks
  skyboxScale: 3,

  // Skybox gradient configurations array
  skyboxGradients: [
    { blueHue: 236.69, blueSaturation: 0.506, blueLightness: 0.4922, whiteR: 1.0, whiteG: 1.0, whiteB: 1.0, blackR: 0.0, blackG: 0.0, blackB: 0.0, threshold1: 0.75, threshold2: 0.5, threshold3: 0.25 },
    { blueHue: 200.0, blueSaturation: 1, blueLightness: 0.5, whiteR: 1.9, whiteG: 1.95, whiteB: 1.0, blackR: 1, blackG: 1, blackB: 1, threshold1: 0.8, threshold2: 0.4, threshold3: 0.2 },
    { blueHue: 200.0, blueSaturation: 1, blueLightness: 0.5, whiteR: 1.9, whiteG: 1.95, whiteB: 1.0, blackR: 0.1, blackG: 0.0, blackB: 0.4, threshold1: 0.8, threshold2: 0.4, threshold3: 0.2 },
    { blueHue: 195.0, blueSaturation: 0.4, blueLightness: 0.45, whiteR: 0.95, whiteG: 0.98, whiteB: 1.1, blackR: 1, blackG: 1, blackB: 1, threshold1: 0.8, threshold2: 0.4, threshold3: 0.2 },    // { blueHue: 203.0, blueSaturation: 1, blueLightness: 0.1, whiteR: 5, whiteG: 5, whiteB: 5, blackR: 1, blackG: 1, blackB: 1, threshold1: 0.8, threshold2: 0.4, threshold3: 0.2 },
    // { blueHue: 103.0, blueSaturation: 0.1, blueLightness: 0.05, whiteR: 0.215, whiteG: 0.215, whiteB: 0.215, blackR: 0.34, blackG: 0.7, blackB: 0.7, threshold1: 0.8, threshold2: 0.4, threshold3: 0.2 },
  ],

  // Active skybox gradient index
  activeSkyboxGradientIndex: (() => {
    try {
      const saved = localStorage.getItem('majaku-privacy-skybox-gradient-index');
      if (saved !== null) {
        const index = parseInt(saved, 10);
        if (!isNaN(index) && index >= 0) {
          return index;
        }
      }
    } catch (error) {
      console.warn('[JS Placement Tower] Failed to load saved gradient index:', error);
    }
    return 7; // Default value
  })(),

  // Shared material for both layers
  material: {
    color: '0xCF0',
    roughness: 0.0,
    metalness: 1,
    emissive: '0xf90',
    emissiveIntensity: 135.7,
    opacity: 0.03,
    alphaTest: 0.01,
    normalScale: 77.0,
  },

  // Layer 1: Explicit position and rotation
  layer1: {
    position: { x: 0, y: -90, z: 0 },
    rotation: { x: -90, y: 0, z: 0 }, // -90 degrees around X axis (horizontal, facing up)
    seamless: 'JS-2-2024-06-02-20-58-28-Seamless.png',
    seamlessNormal: 'JS-2-2024-06-02-20-58-28-Seamless-Normal.png',
  },

  // Layer 2: Explicit position and rotation
  layer2: {
    position: { x: 0, y: 90, z: 0 },
    rotation: { x: -90, y: 0, z: 0 }, // -90 degrees around X axis (horizontal, facing up)
    seamless: 'JS-2-2024-06-02-20-58-28-Seamless.png',
    seamlessNormal: 'JS-2-2024-06-02-20-58-28-Seamless-Normal.png',
  },
};

